import React from 'react'
import styled from 'styled-components'
import { match } from 'ts-pattern'

import { Tooltip } from '@blade/ui-kit'

import type { PairAudit } from '@root/entities/pair'

export type AuditOverviewItem = {
  icon?: React.ReactNode | undefined
  label: React.ReactNode
  value: React.ReactNode

  badge?: React.ReactNode | undefined
  tooltip?: React.ReactNode | undefined
  intent?: PairAudit.AuditStatus
}

type Props = {
  items: Array<AuditOverviewItem>
}

export const TradingAuditOverview: React.FC<Props> = ({ items }) => (
  <Root>
    {items.map((item, idx) => {
      const renderTooltip = (el: React.ReactElement) => {
        if (item.tooltip) {
          return <Tooltip content={item.tooltip}>{el}</Tooltip>
        }
        return el
      }

      return (
        <Row key={idx}>
          {renderTooltip(
            <Label>
              {item.icon}
              {item.label}
              {item.badge}
            </Label>,
          )}
          <Value
            css={`
              --color: var(${intentToColor(item.intent)});
            `}
          >
            {item.value}
          </Value>
        </Row>
      )
    })}
  </Root>
)

const intentToColor = (intent: PairAudit.AuditStatus | undefined) => {
  return match(intent)
    .with('Warning', () => '--warning-9')
    .with('Danger', () => '--error-9')
    .otherwise(() => '--text-11')
}

const Root = styled.div`
  border: 1px solid var(--backgroundA-3);
  background: var(--background-2);

  display: grid;
  border-radius: 12px;
  padding: 1px;
  gap: 2px;

  padding: 10px;
  padding-bottom: 2px;
`

const Row = styled.div`
  height: 35px;
  display: grid;

  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;

  &:not(:last-child) {
    &::after {
      content: '';
      grid-column: 1/-1;
      height: 1px;
      width: 100%;
      opacity: 0.1;

      background-image: radial-gradient(
        circle at center,
        var(--background-11) 0%,
        transparent 100%
      );
    }
  }
`

const Label = styled.span`
  color: var(--text-11);

  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;

  gap: 8px;
  display: grid;
  grid-template-columns: auto auto 1fr;
  align-items: center;
`

const Value = styled.div`
  color: var(--color);

  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  justify-self: end;
  display: grid;

  grid-auto-flow: column;
  grid-auto-columns: auto;
  gap: 4px;
  line-height: 16px;

  a {
    color: var(--color);

    transition: color 0.15s linear;

    &:hover {
      color: var(--text-12);
    }
  }
`
